/**
 * Rentcast Preview — founder-only lab page to see what property enrichment
 * looks like BEFORE buying a Rentcast API key.
 *
 * Type any US address, hit Preview, and we show a nicely-laid-out card of
 * every field Rentcast returns. When RENTCAST_API_KEY is not set on the
 * backend, results are deterministic mock data so the UX is fully explorable.
 * When the key is set, results are live and identically shaped.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  MagnifyingGlass, House, Bed, Bathtub, Ruler, Calendar,
  CurrencyDollar, WarningCircle, CheckCircle, LinkSimple,
} from "@phosphor-icons/react";

const SAMPLE_ADDRESSES = [
  "6220 Carpenter St, Philadelphia, PA 19143",
  "5346 Chew Ave, Philadelphia, PA 19138",
  "1500 Locust St, Philadelphia, PA 19102",
];

function Field({ icon: Icon, label, value, mono = false, fmt }) {
  const display = value == null || value === "" ? "—" : (fmt ? fmt(value) : value);
  return (
    <div className="p-3 bg-white border-2 border-[#0f172a]">
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
        {Icon && <Icon weight="bold" size={12}/>} {label}
      </div>
      <div className={`font-display font-black text-lg ${mono ? "font-mono" : ""}`}>
        {display}
      </div>
    </div>
  );
}

const money = (v) => v == null ? "—" : `$${Number(v).toLocaleString()}`;

export default function RentcastPreview() {
  const [address, setAddress] = useState(SAMPLE_ADDRESSES[0]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ configured: false });

  useEffect(() => {
    api.get("/rentcast/status")
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ configured: false }));
  }, []);

  const preview = async () => {
    if (!address.trim()) { toast.error("Type an address first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/rentcast/enrich", { address: address.trim() });
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Preview failed");
    } finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="rentcast-preview-page">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <House weight="fill" size={28} className="text-[#00C4CC]"/>
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">Rentcast Preview</h1>
          </div>
          <p className="text-sm text-slate-600">
            See what auto-enrichment looks like for a property before you buy an API key.
            {" "}
            {status.configured ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest ml-1">
                <CheckCircle weight="fill" size={10}/> LIVE · key configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-widest ml-1">
                <WarningCircle weight="fill" size={10}/> MOCK · no key yet
              </span>
            )}
          </p>
          {!status.configured && (
            <div className="mt-2 p-3 bg-amber-50 border-2 border-amber-300 text-xs text-amber-900">
              <div className="font-black uppercase tracking-widest text-[10px] mb-1">🔑 Plug in a key to go live</div>
              Set <code className="px-1 bg-white border">RENTCAST_API_KEY</code> in <code className="px-1 bg-white border">/app/backend/.env</code> and restart the backend. Get a key at{" "}
              <a href={status.docs_url || "https://developers.rentcast.io"} target="_blank" rel="noreferrer" className="underline font-black">
                developers.rentcast.io
              </a>
              . {status.cost_note}
            </div>
          )}
        </div>

        <div className="bg-white border-2 border-[#0f172a] p-3 mb-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Address to look up</div>
          <div className="flex gap-2 flex-wrap">
            <input value={address} onChange={e => setAddress(e.target.value)}
              placeholder="6220 Carpenter St, Philadelphia, PA 19143"
              data-testid="rentcast-address"
              className="flex-1 min-w-[280px] h-11 border-2 border-[#0f172a] px-3 text-sm"/>
            <button onClick={preview} disabled={busy}
              data-testid="rentcast-preview-btn"
              className="h-11 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
              <MagnifyingGlass weight="bold" size={14}/>
              {busy ? "Looking up…" : "Preview Enrichment"}
            </button>
          </div>
          <div className="mt-2 flex gap-1 flex-wrap">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1 pt-1">Try:</div>
            {SAMPLE_ADDRESSES.map(a => (
              <button key={a} onClick={() => setAddress(a)}
                data-testid={`rentcast-sample-${a.slice(0,10)}`}
                className="text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-slate-100 hover:bg-slate-200 border border-slate-300">
                {a.split(",")[0]}
              </button>
            ))}
          </div>
        </div>

        {result && (
          <section className="space-y-3" data-testid="rentcast-result">
            <div className="p-3 bg-[#0f172a] text-white flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">Enrichment Result</div>
                <div className="font-display font-black text-base leading-tight truncate">{result.address}</div>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest ${result.mock ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"}`}>
                {result.mock ? "🎭 MOCK DATA" : "✅ LIVE FROM RENTCAST"}
              </span>
            </div>

            {result.not_found ? (
              <div className="p-6 bg-white border-2 border-red-500 text-center">
                <WarningCircle weight="fill" size={32} className="text-red-600 mx-auto mb-2"/>
                <div className="font-display font-black text-lg uppercase">No record found</div>
                <div className="text-xs text-slate-600 mt-1">
                  Rentcast doesn't have this address. Enter fields manually when creating the property.
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Field icon={Bed}          label="Bedrooms"     value={result.bedrooms}/>
                  <Field icon={Bathtub}      label="Bathrooms"    value={result.bathrooms}/>
                  <Field icon={Ruler}        label="Sq Ft"        value={result.square_footage} fmt={v => Number(v).toLocaleString()}/>
                  <Field icon={Calendar}     label="Year Built"   value={result.year_built}/>
                  <Field icon={House}        label="Property Type" value={result.property_type}/>
                  <Field icon={Ruler}        label="Lot Sq Ft"    value={result.lot_size_sqft} fmt={v => Number(v).toLocaleString()}/>
                  <Field icon={CurrencyDollar} label="Last Sale" value={result.last_sale_price} fmt={money}/>
                  <Field icon={Calendar}     label="Sale Date"    value={result.last_sale_date}/>
                </div>

                <div className="p-3 bg-emerald-50 border-2 border-emerald-500">
                  <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-2">💰 Rent Estimate</div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="font-display font-black text-3xl text-emerald-900">
                      {money(result.rent_estimate)}<span className="text-sm font-black text-emerald-700">/mo</span>
                    </div>
                    <div className="text-xs font-black uppercase tracking-widest text-emerald-700">
                      range {money(result.rent_estimate_low)} – {money(result.rent_estimate_high)}
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white border-2 border-[#0f172a] grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Tax Assessment</div>
                    <div className="font-display font-black text-xl">{money(result.tax_assessment)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Source</div>
                    <div className="font-display font-black text-xl uppercase">{result.source}</div>
                  </div>
                </div>

                {result.notes && (
                  <div className={`p-3 text-xs ${result.mock ? "bg-amber-50 border-2 border-amber-300 text-amber-900" : "bg-slate-50 border-2 border-slate-300 text-slate-700"}`}>
                    {result.notes}
                  </div>
                )}
              </>
            )}

            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 pt-1">
              Once wired into New Property flow, these fields will auto-fill on creation so you never re-type sq ft / beds / year built again.
            </div>
          </section>
        )}
      </div>
    </AdminShell>
  );
}
